import React from "react";
import { Select, MenuItem, SelectChangeEvent, Button } from "@mui/material";
import { LABELS, STATUS_OPTIONS } from "./OrderSummaryConstants";

interface OrderStatusSelectorProps {
  status: string;
  orderStatus?: string;
  remainingAmount?: number;
  isFullyPaid?: boolean;
  onChange: (event: SelectChangeEvent<string>) => void;
  onDeliveredClick?: () => void;
}

const OrderStatusSelector: React.FC<OrderStatusSelectorProps> = ({ 
  status, 
  orderStatus, 
  remainingAmount,
  isFullyPaid,
  onChange,
  onDeliveredClick
}) => {
  
  // Filter status options based on payment status
  const getFilteredStatusOptions = () => {
    // If order is cancelled, show no options (read-only)
    if (orderStatus === "Cancelled") {
      return [];
    }
    
    // If order is fully paid, show ONLY "Delivered" option
    if (isFullyPaid) {
      return ["Delivered"];
    }
    
    // If there's remaining amount, remove "Delivered"
    if (remainingAmount && remainingAmount > 0) {
      return STATUS_OPTIONS.filter(option => option !== "Delivered");
    }
    
    return STATUS_OPTIONS;
  };

  const filteredOptions = getFilteredStatusOptions();
  const showDeliveredButton = isFullyPaid && status !== "Delivered" && orderStatus !== "Cancelled";

  // If order is cancelled, show cancelled status as read-only
  if (orderStatus === "Cancelled") {
    return (
      <div className="mb-4">
        <p className="order-status-container">{LABELS.ORDER_STATUS}</p>
        <div className="order-status-canceled">{LABELS.CANCELED}</div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <p className="order-status-container">{LABELS.ORDER_STATUS}</p>
      <div className="space-y-2">
        <Select
          id="order-status"
          value={status}
          fullWidth
          disabled={status === "Delivered"}
          onChange={onChange}
          className="order-status-select"
        >
          {filteredOptions.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </Select>
        
        {/* Delivered Button - Only show when fully paid but not yet delivered */}
        {showDeliveredButton && (
          <Button
            variant="contained"
            color="success"
            fullWidth
            onClick={onDeliveredClick}
            className="bg-green-600 hover:bg-green-700"
          >
            Mark as Delivered
          </Button>
        )}
      </div>
    </div>
  );
};

export default OrderStatusSelector;